import React from 'react';
import Icon from '../../../components/AppIcon';

/**
 * PAY ANALYTICS - The sexy show-off page
 * Credit breakdown + pay categories + visual data
 */
const PayAnalytics = ({ timecard }) => {
  const parsed = timecard?.parsed_json || {};
  const paySummary = parsed.pay_summary || {};
  const creditTotals = paySummary.credit_totals || {};
  const rotations = parsed.rotations || [];
  const additionalPay = paySummary.additional_pay || {};

  const formatTime = (timeStr) => {
    if (!timeStr || timeStr === '0:00') return '0:00';
    return timeStr;
  };

  const timeToHours = (timeStr) => {
    if (!timeStr || timeStr === '0:00') return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours + (minutes / 60);
  };

  // Helper to get pay amount
  const getPayAmount = (key) => {
    if (additionalPay[key]) return additionalPay[key];
    const variations = {
      'g_slip_pay': ['G/SLIP PAY', 'GSLIP PAY'],
      'training_pay': ['DISTRIBUTED TRNG PAY', 'TRAINING PAY']
    };
    if (variations[key]) {
      for (const variant of variations[key]) {
        if (additionalPay[variant]) return additionalPay[variant];
      }
    }
    return null;
  };

  // Calculate totals
  const creditHours = timeToHours(creditTotals.credit_ttl);
  
  const payTimeNoCredit = rotations.reduce((sum, r) => {
    if ((!r.credit_hours || r.credit_hours === '0:00') && r.pay_time && r.pay_time !== '0:00') {
      return sum + timeToHours(r.pay_time);
    }
    return sum;
  }, 0);
  
  const addtlPayOnly = rotations.reduce((sum, r) => {
    if (r.additional_pay_only && r.additional_pay_only !== '0:00') {
      return sum + timeToHours(r.additional_pay_only);
    }
    return sum;
  }, 0);
  
  const gslipHours = timeToHours(getPayAmount('G/SLIP PAY') || '0:00');
  const trainingHours = timeToHours(getPayAmount('DISTRIBUTED TRNG PAY') || '0:00');
  
  const totalPayHours = creditHours + payTimeNoCredit + addtlPayOnly + gslipHours + trainingHours;

  // Credit breakdown for pie chart visualization
  const creditBreakdown = [
    { label: 'Base Credit', value: timeToHours(creditTotals.credit_sub || '0:00'), color: 'bg-accent-cyan', icon: 'Plane' },
    { label: 'Reserve Guar', value: timeToHours(creditTotals.credit_res || '0:00'), color: 'bg-accent-purple', icon: 'Shield' },
    { label: 'Other Guar', value: timeToHours(creditTotals.credit_other || '0:00'), color: 'bg-accent-green', icon: 'Plus' },
    { label: 'Bank Used', value: timeToHours(creditTotals.bank || '0:00'), color: 'bg-warning', icon: 'PiggyBank' }
  ].filter(item => item.value > 0);

  // Additional pay breakdown
  const additionalPayBreakdown = [
    { label: 'Pay Time (no credit)', value: payTimeNoCredit, color: 'bg-warning' },
    { label: 'ADDTL PAY ONLY', value: addtlPayOnly, color: 'bg-accent-purple' },
    { label: 'G/SLIP PAY', value: gslipHours, color: 'bg-accent-green' },
    { label: 'TRAINING PAY', value: trainingHours, color: 'bg-accent-cyan' }
  ].filter(item => item.value > 0);

  const maxCreditValue = Math.max(...creditBreakdown.map(item => item.value));
  const maxAdditionalValue = Math.max(...additionalPayBreakdown.map(item => item.value));

  return (
    <div className="space-y-6">
      {/* HERO STATS - 3 Big Numbers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-6 bg-gradient-to-br from-accent-cyan/20 to-accent-cyan/5 border-accent-cyan/40">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Award" className="w-8 h-8 text-accent-cyan" />
            <h3 className="text-sm font-medium text-muted-foreground uppercase">Total Pay Hours</h3>
          </div>
          <p className="text-5xl font-bold text-accent-cyan mb-1">
            {Math.floor(totalPayHours)}:{String(Math.round((totalPayHours - Math.floor(totalPayHours)) * 60)).padStart(2, '0')}
          </p>
          <p className="text-sm text-muted-foreground">{totalPayHours.toFixed(2)} hours total</p>
        </div>

        <div className="card p-6 bg-gradient-to-br from-accent-green/20 to-accent-green/5 border-accent-green/40">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="TrendingUp" className="w-8 h-8 text-accent-green" />
            <h3 className="text-sm font-medium text-muted-foreground uppercase">Credit Hours</h3>
          </div>
          <p className="text-5xl font-bold text-accent-green mb-1">{formatTime(creditTotals.credit_ttl)}</p>
          <p className="text-sm text-muted-foreground">{creditHours.toFixed(2)} hours</p>
        </div>

        <div className="card p-6 bg-gradient-to-br from-warning/20 to-warning/5 border-warning/40">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="DollarSign" className="w-8 h-8 text-warning" />
            <h3 className="text-sm font-medium text-muted-foreground uppercase">Additional Pay</h3>
          </div>
          <p className="text-5xl font-bold text-warning mb-1">
            {Math.floor(totalPayHours - creditHours)}:{String(Math.round(((totalPayHours - creditHours) - Math.floor(totalPayHours - creditHours)) * 60)).padStart(2, '0')}
          </p>
          <p className="text-sm text-muted-foreground">{(totalPayHours - creditHours).toFixed(2)} hours</p>
        </div>
      </div>

      {/* CREDIT HOURS BREAKDOWN - Visual Bars */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="BarChart3" className="w-5 h-5 text-accent-cyan" />
          Credit Hours Breakdown
        </h3>
        
        <div className="space-y-4">
          {creditBreakdown.map((item, index) => {
            const percentage = (item.value / creditHours) * 100;
            const barWidth = (item.value / maxCreditValue) * 100;
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name={item.icon} className={`w-4 h-4 text-${item.color.replace('bg-', '')}`} />
                    <span className="text-sm font-medium text-foreground">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-mono text-muted-foreground">
                      {Math.floor(item.value)}:{String(Math.round((item.value - Math.floor(item.value)) * 60)).padStart(2, '0')}
                    </span>
                    <span className="text-sm font-bold text-accent-cyan w-12 text-right">
                      {percentage.toFixed(0)}%
                    </span>
                  </div>
                </div>
                <div className="relative h-8 bg-muted/30 rounded-lg overflow-hidden">
                  <div 
                    className={`absolute inset-y-0 left-0 ${item.color} opacity-80 transition-all duration-500 ease-out flex items-center justify-end pr-3`}
                    style={{ width: `${barWidth}%` }}
                  >
                    <span className="text-xs font-bold text-white drop-shadow-lg">
                      {item.value.toFixed(1)}h
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Credit Total */}
        <div className="mt-6 pt-4 border-t-2 border-accent-cyan/30">
          <div className="flex items-center justify-between bg-accent-cyan/10 rounded-lg p-4">
            <span className="text-lg font-bold text-foreground uppercase">Total Credit</span>
            <div className="text-right">
              <p className="text-3xl font-bold text-accent-cyan">{formatTime(creditTotals.credit_ttl)}</p>
              <p className="text-sm text-muted-foreground">{creditHours.toFixed(2)} hours</p>
            </div>
          </div>
        </div>
      </div>

      {/* ADDITIONAL PAY BREAKDOWN - Visual Bars */}
      {additionalPayBreakdown.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
            <Icon name="PlusCircle" className="w-5 h-5 text-warning" />
            Additional Pay Breakdown
          </h3>
          
          <div className="space-y-4">
            {additionalPayBreakdown.map((item, index) => {
              const percentage = totalPayHours > 0 ? (item.value / totalPayHours) * 100 : 0;
              const barWidth = (item.value / maxAdditionalValue) * 100;
              
              return (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{item.label}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono text-muted-foreground">
                        {Math.floor(item.value)}:{String(Math.round((item.value - Math.floor(item.value)) * 60)).padStart(2, '0')}
                      </span>
                      <span className="text-sm font-bold text-warning w-12 text-right">
                        {percentage.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <div className="relative h-8 bg-muted/30 rounded-lg overflow-hidden">
                    <div 
                      className={`absolute inset-y-0 left-0 ${item.color} opacity-80 transition-all duration-500 ease-out flex items-center justify-end pr-3`}
                      style={{ width: `${barWidth}%` }}
                    >
                      <span className="text-xs font-bold text-white drop-shadow-lg">
                        {item.value.toFixed(1)}h
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ROTATION STATS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-5 bg-accent-purple/5 border-accent-purple/20">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Plane" className="w-6 h-6 text-accent-purple" />
            <h4 className="text-sm font-medium text-muted-foreground uppercase">Total Rotations</h4>
          </div>
          <p className="text-4xl font-bold text-accent-purple">{rotations.length}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {rotations.filter(r => r.credit_hours && r.credit_hours !== '0:00').length} with credit
          </p>
        </div>

        <div className="card p-5 bg-warning/5 border-warning/20">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Clock" className="w-6 h-6 text-warning" />
            <h4 className="text-sm font-medium text-muted-foreground uppercase">Avg Per Rotation</h4>
          </div>
          <p className="text-4xl font-bold text-warning">
            {rotations.length > 0 ? (creditHours / rotations.length).toFixed(1) : '0.0'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">hours credit per trip</p>
        </div>

        <div className="card p-5 bg-accent-green/5 border-accent-green/20">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="ArrowRightLeft" className="w-6 h-6 text-accent-green" />
            <h4 className="text-sm font-medium text-muted-foreground uppercase">Spillovers</h4>
          </div>
          <p className="text-4xl font-bold text-accent-green">
            {rotations.filter(r => r.is_spillover).length}
          </p>
          <p className="text-xs text-muted-foreground mt-1">rotations spanning months</p>
        </div>
      </div>

      {/* INFO BOX */}
      <div className="card p-5 bg-accent-cyan/5 border-accent-cyan/20">
        <div className="flex items-start gap-3">
          <Icon name="Info" className="w-5 h-5 text-accent-cyan flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-semibold text-foreground mb-1">How Total Pay Hours Works</p>
            <p>
              <strong className="text-accent-cyan">Credit Hours</strong> ({formatTime(creditTotals.credit_ttl)}) come from rotations with credit, plus bank hours used this month.
              <br />
              <strong className="text-warning">Additional Pay</strong> includes rotations without credit (pay time only), extra pay categories like G/SLIP and training, and any additional pay from individual rotations.
              <br />
              <strong className="text-accent-green">Total Pay Hours</strong> is what you multiply by your hourly rate to get your total paycheck.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayAnalytics;
